import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

import { useSlateTableRegistry } from "../../contexts/SlateTableRegistryContext";
import { useTableConfig } from "../../hooks/useTableConfig";
import type {
  TableConfig,
  SlateTableRow,
  RowAction,
  Column,
} from "../../types/table";
import { MOCK_ROWS } from "../../types/table";

import { SlateTable } from "./SlateTable";
import type { CustomCellRenderer, TableSortDirection } from "./SlateTable";
import { SlateTableConfigPanel } from "./SlateTableConfigPanel";

// ─── Props ────────────────────────────────────────────────────────────────────

interface SlateTableWithConfigProps {
  /** Stable ID for persistence — config auto-saves and auto-loads by this key */
  tableId?: string;
  /** Seed config for first mount — persisted immediately, then fully editable. Ignored if a saved config already exists for this tableId. */
  initialConfig?: TableConfig;
  /** Custom row data (defaults to MOCK_ROWS) */
  rows?: SlateTableRow[];
  /** Show the floating config toggle button (default: true) */
  configurable?: boolean;
  /** Allow inline cell editing (default: false) */
  editable?: boolean;
  /** Callback when a row action is clicked */
  onActionClick?: (action: RowAction, rowId: string) => void;
  /** When true for a row, the row shows pointer cursor and receives click events */
  isRowClickable?: (rowId: string) => boolean;
  /** Called when a clickable row is clicked */
  onRowClick?: (rowId: string) => void;
  /** Override cell rendering for specific types or values */
  customCellRenderer?: CustomCellRenderer;
  /** Make the table header sticky when scrolling */
  stickyHeader?: boolean;
  /** When true, never load/save config — always use initialConfig. Use for tables that shouldn't persist. */
  disablePersistence?: boolean;
  /** When true, show a green toolbar above the table when rows are selected (products table) */
  selectionToolbar?: boolean;
  /** Label for the selection count (e.g. "products") */
  selectionToolbarLabel?: string;
  /** FBF filter value: "all" | "needs-setup" | "configured" — controls Setup for FBF and Create shipment link visibility */
  selectionToolbarFbfFilter?: "all" | "needs-setup" | "configured";
  /** Called when Bulk edit is clicked in the selection toolbar */
  onBulkEditClick?: () => void;
  /** Column ids that are sortable — only these columns get the clickable header treatment */
  sortableColumnIds?: string[];
  /** Called when a sortable column header is clicked */
  onColumnHeaderClick?: (colId: string) => void;
  /** Column id that is currently sorted — shows a sort indicator */
  sortedColumnId?: string;
  /** Sort direction for the active sorted column */
  sortedColumnDirection?: TableSortDirection;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SlateTableWithConfig({
  tableId,
  initialConfig,
  rows = MOCK_ROWS,
  configurable = true,
  editable = false,
  onActionClick,
  isRowClickable,
  onRowClick,
  customCellRenderer,
  stickyHeader = false,
  disablePersistence = false,
  selectionToolbar = false,
  selectionToolbarLabel = "products",
  selectionToolbarFbfFilter,
  onBulkEditClick,
  sortableColumnIds,
  onColumnHeaderClick,
  sortedColumnId,
  sortedColumnDirection,
}: SlateTableWithConfigProps) {
  // Namespace tableId with the page path to prevent collisions across prototypes
  const location = useLocation();
  const effectiveTableId = disablePersistence ? undefined : tableId;
  const namespacedTableId = effectiveTableId
    ? `${location.pathname}::${effectiveTableId}`
    : undefined;
  const internalId = useRef(
    namespacedTableId ?? `table-${Math.random().toString(36).slice(2, 8)}`
  ).current;
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    config: hookConfig,
    setConfig,
    configName,
    setConfigName,
    savedConfigs,
    configLoaded,
    saveConfig,
    loadConfig,
    deleteConfig,
  } = useTableConfig(namespacedTableId, tableId, initialConfig);

  function handleConfigChange(newConfig: TableConfig) {
    setConfig(newConfig);
  }

  // ── Registry integration ───────────────────────────────────────────────────
  const registry = useSlateTableRegistry();

  // Keep a ref to registry so callbacks/effects don't depend on the unstable
  // context value object, which changes reference on every Provider render and
  // would otherwise cause an infinite update loop: toggle → update → re-render
  // → new registry ref → callbacks recreated → effect fires → update → …
  const registryRef = useRef(registry);
  registryRef.current = registry;

  // Derive open state from registry (single source of truth)
  const showConfigPanel = registry
    ? registry.activeConfigId === internalId
    : false;
  // Fallback local state for when no registry exists
  const [localShowConfig, setLocalShowConfig] = useState(false);
  const isConfigOpen = registry ? showConfigPanel : localShowConfig;

  const setConfigOpen = useCallback(
    (open: boolean) => {
      const reg = registryRef.current;
      if (reg) {
        reg.setActiveConfigId(open ? internalId : null);
      } else {
        setLocalShowConfig(open);
      }
    },
    [internalId]
  );

  const toggleConfig = useCallback(() => {
    const reg = registryRef.current;
    if (reg) {
      const isOpen = reg.activeConfigId === internalId;
      reg.setActiveConfigId(isOpen ? null : internalId);
    } else {
      setLocalShowConfig((prev) => !prev);
    }
  }, [internalId]);

  // ── Scroll to table ────────────────────────────────────────────────────────
  const scrollToTable = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 40;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  // Register on mount, unregister on unmount
  useEffect(() => {
    const reg = registryRef.current;
    if (!configurable || !reg) return;
    reg.register({
      id: internalId,
      name: configName,
      onToggleConfig: toggleConfig,
      onScrollTo: scrollToTable,
      isConfigOpen,
    });
    return () => reg.unregister(internalId);
  }, [configurable, internalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep registry in sync with name and open state
  useEffect(() => {
    const reg = registryRef.current;
    if (!configurable || !reg) return;
    reg.update(internalId, {
      name: configName,
      onToggleConfig: toggleConfig,
      onScrollTo: scrollToTable,
      isConfigOpen,
    });
  }, [configName, isConfigOpen, configurable, internalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Don't render until config is loaded (prevents flash of default config)
  if (namespacedTableId && !configLoaded) {
    return null;
  }

  // Hide the floating gear button when the registry is available (VariantSwitcher handles it)
  const showFloatingButton =
    configurable && !isConfigOpen && !registryRef.current;

  // When persistence is disabled, always use the latest initialConfig (e.g. when filtering changes rowCount)
  const config =
    disablePersistence && initialConfig ? initialConfig : hookConfig;

  return (
    <div className="relative" ref={containerRef}>
      {/* Table renderer */}
      <SlateTable
        config={config}
        rows={rows}
        editable={editable || isConfigOpen}
        highlighted={isConfigOpen}
        customCellRenderer={customCellRenderer}
        stickyHeader={stickyHeader}
        selectionToolbar={selectionToolbar}
        selectionToolbarLabel={selectionToolbarLabel}
        selectionToolbarFbfFilter={selectionToolbarFbfFilter}
        onBulkEditClick={onBulkEditClick}
        sortableColumnIds={sortableColumnIds}
        onColumnHeaderClick={onColumnHeaderClick}
        sortedColumnId={sortedColumnId}
        sortedColumnDirection={sortedColumnDirection}
        onConfigChange={handleConfigChange}
        onActionClick={onActionClick}
        isRowClickable={isRowClickable}
        onRowClick={onRowClick}
      />

      {/* Floating config toggle button (fallback when no VariantSwitcher/registry) */}
      {showFloatingButton && (
        <button
          onClick={() => setConfigOpen(true)}
          className="fixed bottom-6 left-6 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-[#111827] text-(--slate-color-action-text-inverse) shadow-lg transition-colors hover:bg-[#374151]"
          title="Configure table"
        >
          <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor">
            <path d="M128,80a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Zm88-29.84q.06-2.16,0-4.32l14.92-18.64a8,8,0,0,0,1.48-7.06,107.21,107.21,0,0,0-10.88-26.25,8,8,0,0,0-6-3.93l-23.72-2.64q-1.48-1.56-3-3L186,40.54a8,8,0,0,0-3.94-6,107.71,107.71,0,0,0-26.25-10.87,8,8,0,0,0-7.06,1.49L130.16,40Q128,40,125.84,40L107.2,25.11a8,8,0,0,0-7.06-1.48A107.6,107.6,0,0,0,73.89,34.51a8,8,0,0,0-3.93,6L67.32,64.27q-1.56,1.49-3,3L40.54,70a8,8,0,0,0-6,3.94,107.71,107.71,0,0,0-10.87,26.25,8,8,0,0,0,1.49,7.06L40,125.84Q40,128,40,130.16L25.11,148.8a8,8,0,0,0-1.48,7.06,107.21,107.21,0,0,0,10.88,26.25,8,8,0,0,0,6,3.93l23.72,2.64q1.49,1.56,3,3L70,215.46a8,8,0,0,0,3.94,6,107.71,107.71,0,0,0,26.25,10.87,8,8,0,0,0,7.06-1.49L125.84,216q2.16.06,4.32,0l18.64,14.92a8,8,0,0,0,7.06,1.48,107.21,107.21,0,0,0,26.25-10.88,8,8,0,0,0,3.93-6l2.64-23.72q1.56-1.48,3-3L215.46,186a8,8,0,0,0,6-3.94,107.71,107.71,0,0,0,10.87-26.25,8,8,0,0,0-1.49-7.06Zm-16.1-6.5a73.93,73.93,0,0,1,0,8.68,8,8,0,0,0,1.74,5.68l14.19,17.73a91.57,91.57,0,0,1-6.23,15L187,173.11a8,8,0,0,0-5.1,2.64,74.11,74.11,0,0,1-6.14,6.14,8,8,0,0,0-2.64,5.1l-2.51,22.58a91.32,91.32,0,0,1-15,6.23l-17.74-14.19a8,8,0,0,0-5-1.75h-.67a73.68,73.68,0,0,1-8.68,0,8,8,0,0,0-5.68,1.74L100.45,215.8a91.57,91.57,0,0,1-15-6.23L82.89,187a8,8,0,0,0-2.64-5.1,74.11,74.11,0,0,1-6.14-6.14,8,8,0,0,0-5.1-2.64L46.43,170.6a91.32,91.32,0,0,1-6.23-15l14.19-17.74a8,8,0,0,0,1.74-5.68,73.93,73.93,0,0,1,0-8.68,8,8,0,0,0-1.74-5.68L40.2,100.45a91.57,91.57,0,0,1,6.23-15L69,82.89a8,8,0,0,0,5.1-2.64,74.11,74.11,0,0,1,6.14-6.14A8,8,0,0,0,82.89,69L85.4,46.43a91.32,91.32,0,0,1,15-6.23l17.74,14.19a8,8,0,0,0,5.68,1.74,73.68,73.68,0,0,1,8.68,0,8,8,0,0,0,5.68-1.74L155.55,40.2a91.57,91.57,0,0,1,15,6.23L173.11,69a8,8,0,0,0,2.64,5.1,74.11,74.11,0,0,1,6.14,6.14,8,8,0,0,0,5.1,2.64l22.58,2.51a91.32,91.32,0,0,1,6.23,15l-14.19,17.74A8,8,0,0,0,199.87,123.66Z" />
          </svg>
        </button>
      )}

      {/* Config panel overlay */}
      {configurable && isConfigOpen && (
        <SlateTableConfigPanel
          config={hookConfig}
          configName={configName}
          onConfigChange={handleConfigChange}
          onConfigNameChange={setConfigName}
          onSave={saveConfig}
          onClose={() => setConfigOpen(false)}
          savedConfigs={savedConfigs}
          onLoad={loadConfig}
          onDelete={deleteConfig}
          onScrollToTable={scrollToTable}
        />
      )}
    </div>
  );
}
